import { call, select } from 'typed-redux-saga'
import { AnyAction } from 'redux'

import { ApplicationState } from '@store/types'
import { StepRefin } from '@utils/enums'
import services from '@services'
import { TrackRefinRequest } from '@services/postTrackingRefinService'
import { initTrackRefin, isErroStep, logTrackRefinStep } from '@utils/trackRefin'

import { TrackingRefinPayload } from '../../simulacao/actions'
import { buildTrackingRequest } from '../models'

const normalizeStep = (payload: unknown): StepRefin | string => {
  if (typeof payload === 'string') return payload
  if (payload == null) return ''
  return String(payload)
}

const isTrackingRefinPayload = (payload: unknown): payload is TrackingRefinPayload => {
  if (payload == null || typeof payload !== 'object') return false
  return 'step' in payload
}

const getTrackingPayload = (payload: unknown): TrackingRefinPayload => {
  if (isTrackingRefinPayload(payload)) {
    return {
      ...payload,
      step: normalizeStep(payload.step),
    }
  }

  return { step: normalizeStep(payload) }
}

const maskCpf = (raw: string): string => {
  const digits = raw.replace(/\D/g, '')
  if (digits.length !== 11) return 'unknown'
  return `***.${digits.substring(3, 6)}.${digits.substring(6, 9)}-**`
}

const buildTrackData = (tracking: TrackRefinRequest): Record<string, string> => ({
  cpf: maskCpf(tracking.documento),
  numeroContrato: tracking.numeroContrato,
  valorEmprestimo: String(tracking.valorEmprestimo),
  valorLiberado: String(tracking.valorLiberado),
  valorQuitacao: String(tracking.valorQuitacao),
  valorTotalSolicitado: String(tracking.valorTotalSolicitado),
  valorTotalRefinanciado: String(tracking.valorTotalRefinanciado),
  saldoDevedorPreRefin: String(tracking.saldoDevedorPreRefin),
  quantidadeParcelas: String(tracking.quantidadeParcelas),
  taxaJuros: String(tracking.taxaJuros),
  iof: String(tracking.iof),
  valorCET: String(tracking.valorCET),
  custoEfetivoTotal: String(tracking.custoEfetivoTotal),
})

export default function* postTrackingRefinSaga(action: AnyAction) {
  const payload = getTrackingPayload(action.payload)
  const { step, screen_name, section, scenario } = payload

  try {
    const state = yield* select((s: ApplicationState) => s)
    const tracking: TrackRefinRequest = buildTrackingRequest(state, step, {
      screen_name,
      section,
      scenario,
    })

    const { sessionId } = tracking
    const isApp = state.ui?.navigation?.isApp ?? false

    if (sessionId) {
      initTrackRefin(sessionId, isApp)
    }

    const apiOutcomeData: Record<string, string> = isErroStep(step)
      ? {
          codigoResposta: state.session?.lastServiceCode ?? '',
          servico: state.session?.lastServiceName ?? '',
        }
      : {}

    logTrackRefinStep(step, {
      ...buildTrackData(tracking),
      ...apiOutcomeData,
    })

    yield* call(services.postTrackingRefinService, tracking)
  } catch {
    logTrackRefinStep(StepRefin.ERRO_ENVIO_TRACKING, { etapaOriginal: String(step) })
  }
}
